import React, { useEffect } from 'react';
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  Outlet,
  useLocation,
} from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';

import { auth } from './lib/firebase';
import useAuthStore, { clearUserScopedState } from './stores/authStore';
import { useUiStore } from './v2/theme/uiStore';
import { useApplyTheme } from './v2/theme/useApplyTheme';
import { roleToBrand } from './v2/branding/registry';
import { V2Layout } from './v2/layout/V2Layout';
import { v2Routes } from './v2/V2Routes';
import { isClassicShellRoute } from './lib/routes';
import {
  clearReturnPath,
  hasStoredReturnPath,
  loginUrlFor,
  readReturnPath,
  stashReturnPath,
} from './lib/postLoginReturn';

/* --- Page Components --- */
import V2LoginPage from './v2/pages/V2LoginPage';
import V2SignupPage from './v2/pages/V2SignupPage';
import RoofPreviewPage from './pages/RoofPreviewPage';
import DashboardPage from './pages/DashboardPage';
import ProjectDetailPage from './pages/ProjectDetailPage';
import ChatPage from './pages/ChatPage';
import ProfilePage from './pages/ProfilePage';
import ScopeBuilderPage from './pages/ScopeBuilderPage';
import ScopeReviewPage from './pages/ScopeReviewPage';
import AnalysisDashboardPage from './pages/AnalysisDashboardPage';
import CompletenessPage from './pages/CompletenessPage';
import AdminPage from './pages/AdminPage';
import RulesPage from './pages/RulesPage';
import RuleReviewPage from './pages/RuleReviewPage';
import RuleSuggestionsPage from './pages/RuleSuggestionsPage';
import AdminRuleReviewPage from './pages/AdminRuleReviewPage';
import AdminTriagePage from './pages/AdminTriagePage';
import AdminReviewPage from './pages/admin/AdminReviewPage';   /* PRP-MCP-9: assistant snapshots */
import AdminSuggestionsPage from './pages/admin/AdminSuggestionsPage';   /* PRP-MCP-12: suggestions from chat */
import AdminUserPage from './pages/admin/AdminUserPage';   /* PRP-MCP-9 G7: one user's access */
import AdminAffiliatesPage from './pages/admin/AdminAffiliatesPage';   /* PRP-MCP-10 G1 */
import AdminAffiliatePage from './pages/admin/AdminAffiliatePage';
import ComparisonsPage from './pages/ComparisonsPage';
import ComparisonReportPage from './pages/ComparisonReportPage';
/* PRP-MCP-1: proposal review + OAuth consent (shared by both shells). */
import V2McpProposalsPage from './v2/pages/V2McpProposalsPage';
import V2McpAuthorizePage from './v2/pages/V2McpAuthorizePage';
import V2AssistantPage from './v2/pages/V2AssistantPage';
import V2MacrosPage from './v2/pages/V2MacrosPage';
import V2WelcomePage from './v2/pages/V2WelcomePage';
/* PRP-CALC-1: public, no-auth equipment cost calculators (/tools). */
import ToolsIndexPage from './pages/tools/ToolsIndexPage';
import CraneCalculatorPage from './pages/tools/CraneCalculatorPage';
import ScaffoldCalculatorPage from './pages/tools/ScaffoldCalculatorPage';
import AerialLiftCalculatorPage from './pages/tools/AerialLiftCalculatorPage';
import WaterMitigationCalculatorPage from './pages/tools/WaterMitigationCalculatorPage';
import DrywallCalculatorPage from './pages/tools/DrywallCalculatorPage';
import DumpsterCalculatorPage from './pages/tools/DumpsterCalculatorPage';

/* --- Layout Components --- */
import Sidebar from './components/layout/Sidebar';
import Header from './components/layout/Header';
import ProjectContextHeader from './components/layout/ProjectContextHeader';
import { Toaster } from './components/ui/Toaster';

const devBypass = import.meta.env.DEV && import.meta.env.VITE_DEV_AUTH_BYPASS === 'true';

// Reason: Surface the dev auth bypass in the tab title so it can't be missed.
// Only when active — otherwise production keeps the real title from index.html.
if (devBypass && typeof document !== 'undefined') {
  document.title = 'BYPASS-ACTIVE';
}

// --- Shared loading indicator ---
const LoadingScreen = () => (
  <div className="flex h-screen items-center justify-center">Loading...</div>
);

// --- App shell layout (used by ProtectedRoute) ---
const MainLayout = () => (
  <div className="flex h-screen bg-background text-foreground">
    <Sidebar />
    <div className="flex flex-1 flex-col overflow-hidden">
      <Header />
      <main className="flex min-h-0 flex-1 flex-col overflow-hidden">
        {/* Persistent project context strip. Reads `?project=<id>` from
            the URL and mirrors into projectStore. Sits above the scroll
            area as a fixed-height flex item so full-height pages (Chat)
            get the exact remaining height. See ProjectContextHeader. */}
        <ProjectContextHeader />
        {/* Scroll container. flex-1 + min-h-0 give it a definite height,
            so pages can either scroll their own content here or fill it
            with `h-full` instead of brittle 100vh calc math. */}
        <div className="min-h-0 flex-1 overflow-y-auto p-4 md:p-6 lg:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  </div>
);

// App routes: must be logged in. Role-less users are pay-per-conversion
// customers — they get the FULL V2 shell (dashboard, Supplement import, ESX
// export) with member features locked, instead of the old limited /try page.
// While a KNOWN role-less user's V2-force effect hasn't committed yet, hold
// the loading screen — otherwise the classic (lock-less) tree renders for one
// commit before the effect flips v2Enabled (B-008 family).
export const ProtectedRoute = () => {
  const { user, loading, roles, rolesLoaded, rolesFetchFailed } = useAuthStore();
  const v2 = useUiStore((s) => s.v2Enabled);
  const location = useLocation();
  // PRP-MCP-11 G3: the user has landed on a protected page signed in, so the
  // stored return path has done its job.
  useEffect(() => {
    if (user && !loading && hasStoredReturnPath()) clearReturnPath();
  }, [user, loading]);
  if (devBypass) return <Outlet />;
  if (loading) return <LoadingScreen />;
  if (!user) {
    // PRP-MCP-11 G3 (was PRP-MCP-1's consent-screen-only stash): remember the
    // linked page in a cookie AND in ?next= so sign-in comes back to it.
    const here = location.pathname + location.search + location.hash;
    stashReturnPath(here);
    return <Navigate to={loginUrlFor(here)} replace />;
  }
  if (!rolesLoaded) return <LoadingScreen />;
  if (roles.length === 0 && !rolesFetchFailed && !v2) return <LoadingScreen />;
  return <Outlet />;
};

// Legacy /try entry point (marketing CTAs still link here): authenticated
// users land in the app; everyone else goes to signup. Query params are
// forwarded — in-flight Stripe sessions created before the URL change carry
// ?purchase=... here.
export const TryRedirect = () => {
  const { user, loading } = useAuthStore();
  const location = useLocation();
  if (loading) return <LoadingScreen />;
  return (
    <Navigate
      to={{ pathname: user ? '/' : '/signup', search: location.search }}
      replace
    />
  );
};

// Admin + rule screens have not been redesigned for V2. They render in the
// classic MainLayout shell even while V2 is active (see AppShell), so they look
// correct without flipping the whole app to classic. Declared once and shared
// between the v2-on and v2-off branches.
function adminClassicRoutes() {
  return (
    <>
      <Route path="/admin/analysis" element={<AnalysisDashboardPage />} />
      <Route path="/admin/rules" element={<RulesPage />} />
      <Route path="/admin/rule-suggestions" element={<RuleSuggestionsPage />} />
      <Route path="/admin/rule-reviews" element={<AdminRuleReviewPage />} />
      <Route path="/admin/triage" element={<AdminTriagePage />} />
      <Route path="/admin/review" element={<AdminReviewPage />} />
      <Route path="/admin/suggestions" element={<AdminSuggestionsPage />} />
      <Route path="/admin/users/:id" element={<AdminUserPage />} />
      <Route path="/admin/affiliates" element={<AdminAffiliatesPage />} />
      <Route path="/admin/affiliates/:id" element={<AdminAffiliatePage />} />
      <Route path="/admin" element={<AdminPage />} />
      <Route path="/review/rules" element={<RuleReviewPage />} />
    </>
  );
}

// The routed shell. Rendered inside <BrowserRouter> so it can read the current
// pathname: classic-shell routes (admin/rules) render in MainLayout and get the
// classic palette (V2 data-* attributes stripped) WITHOUT persisting the global
// V2-off toggle. Everything else uses V2Layout when V2 is enabled.
function AppShell() {
  const user = useAuthStore((s) => s.user);
  const loading = useAuthStore((s) => s.loading);
  const v2 = useUiStore((s) => s.v2Enabled);
  const theme = useUiStore((s) => s.theme);
  const { pathname, search } = useLocation();

  // V2 chrome applies only when V2 is on AND we're not on a classic-shell route.
  // On classic-shell routes (admin/rule/review) we instead toggle the `.dark`
  // class when the user runs V2-dark, so those screens match the rest of the
  // site's theme without the V2 data-* tokens (different palette). B-615.
  const onClassicShell = isClassicShellRoute(pathname);
  // Toggle the shadcn `.dark` token block whenever the site is in dark mode —
  // NOT only on classic-shell routes. The V2 `data-theme` tokens (v2-tokens.css)
  // don't define the shadcn variables (--card/--foreground/--background/--muted/
  // --border), so V2 routes built with shadcn tokens or Tailwind `dark:`
  // variants (Comparisons, Tools) rendered light-on-dark until `.dark` was set.
  // V2's `:root[data-theme="dark"]` selectors out-specify `.dark`, so the V2
  // chrome/accent is unaffected; `.dark` only fills in the shadcn tokens. B-685.
  useApplyTheme(v2 && !onClassicShell, v2 && theme === 'dark');

  return (
    <Routes>
      <Route path="/login" element={
        devBypass ? (
          <Navigate to="/" />
        ) : loading ? (
          // Reason: Firebase restores cached auth from IndexedDB
          // asynchronously after reload. Without this guard, /login
          // briefly renders the form before onAuthStateChanged fires
          // and navigates to /.
          <div className="flex h-screen items-center justify-center">Loading...</div>
        ) : user ? (
          // PRP-MCP-11 G3: back to the linked page (?next= or the cookie), else the dashboard.
          <Navigate to={readReturnPath(search) ?? '/'} replace />
        ) : (
          <V2LoginPage />
        )
      } />
      <Route path="/signup" element={<V2SignupPage />} />
      {/* PRP-I5: public roof-XML -> on-screen 2D plan preview (local visual proof). */}
      <Route path="/roof-preview" element={<RoofPreviewPage />} />
      {/* PRP-CALC-1: public equipment cost calculators (no auth). */}
      <Route path="/tools" element={<ToolsIndexPage />} />
      <Route path="/tools/crane-cost-estimator" element={<CraneCalculatorPage />} />
      <Route
        path="/tools/scaffolding-cost-calculator"
        element={<ScaffoldCalculatorPage />}
      />
      <Route
        path="/tools/aerial-lift-cost-calculator"
        element={<AerialLiftCalculatorPage />}
      />
      <Route
        path="/tools/water-mitigation-calculator"
        element={<WaterMitigationCalculatorPage />}
      />
      <Route
        path="/tools/drywall-repair-calculator"
        element={<DrywallCalculatorPage />}
      />
      <Route
        path="/tools/dumpster-weight-calculator"
        element={<DumpsterCalculatorPage />}
      />
      {/* Legacy trial entry: marketing CTAs link here; redirect into the app. */}
      <Route path="/try" element={<TryRedirect />} />
      <Route element={<ProtectedRoute />}>
        {v2 ? (
          <>
            <Route element={<V2Layout />}>{v2Routes()}</Route>
            <Route element={<MainLayout />}>{adminClassicRoutes()}</Route>
          </>
        ) : (
          <Route element={<MainLayout />}>
            <Route path="/" element={<DashboardPage />} />
            <Route path="/project/:id" element={<ProjectDetailPage />} />
            <Route path="/chat" element={<ChatPage />} />
            <Route path="/chat/:projectId" element={<ChatPage />} />
            <Route path="/scope/builder" element={<ScopeBuilderPage />} />
            <Route path="/scope/review" element={<ScopeReviewPage />} />
            <Route path="/completeness" element={<CompletenessPage />} />
            <Route path="/comparisons" element={<ComparisonsPage />} />
            <Route path="/comparisons/:id" element={<ComparisonReportPage />} />
            {adminClassicRoutes()}
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/mcp/proposals" element={<V2McpProposalsPage />} />
            <Route path="/mcp/authorize" element={<V2McpAuthorizePage />} />
            <Route path="/assistant" element={<V2AssistantPage />} />
            <Route path="/macros" element={<V2MacrosPage />} />
            <Route path="/welcome" element={<V2WelcomePage />} />
          </Route>
        )}
      </Route>
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
}

/** How many times the role fetch is attempted before the session gives up (B-910). */
const ROLE_FETCH_ATTEMPTS = 4;
/** Base delay between role-fetch attempts, in ms; each attempt waits one more multiple. */
const ROLE_FETCH_RETRY_MS = 1500;

function App() {
  const { setUser, setLoading, setRoles, setRolesLoaded } = useAuthStore();
  const user = useAuthStore((s) => s.user);
  const roles = useAuthStore((s) => s.roles);

  // Role is the authoritative source of the white-label brand: a carrier role
  // forces (and, via the gated picker, locks) that carrier's skin. Non-carrier
  // users keep whatever brand they manually picked. Minimal/additive.
  useEffect(() => {
    const rb = roleToBrand(roles);
    if (rb !== 'standard') useUiStore.getState().setBrand(rb);
  }, [roles]);

  // Pay-per-conversion (role-less) users always get the V2 shell — the classic
  // UI has no locks/credits surface for them. Role users keep their toggle.
  // NEVER force on a roles-fetch FAILURE: a member on a flaky connection must
  // not have their UI preference persistently overwritten.
  const rolesLoaded = useAuthStore((s) => s.rolesLoaded);
  const rolesFetchFailed = useAuthStore((s) => s.rolesFetchFailed);
  useEffect(() => {
    if (user && rolesLoaded && !rolesFetchFailed && roles.length === 0) {
      useUiStore.getState().enableV2();
    }
  }, [user, rolesLoaded, rolesFetchFailed, roles]);

  useEffect(() => {
    if (devBypass) {
      setUser({
        id: 'dev-user',
        email: 'dev@test.com',
        displayName: 'Dev User',
        photoURL: null,
        emailVerified: true,
      });
      setLoading(false);
      return;
    }

    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      if (firebaseUser) {
        // B-509: if a DIFFERENT account than last time signs in on this browser,
        // wipe the prior account's per-user stores (selected project + PII,
        // in-progress imports) BEFORE rendering, so nothing bleeds across the
        // switch. A same-user cold reload (lastUid === uid) keeps its persisted
        // selection. First-ever login (no lastUid) has nothing to clear.
        try {
          const lastUid = localStorage.getItem('aiea-last-uid');
          if (lastUid && lastUid !== firebaseUser.uid) clearUserScopedState();
          localStorage.setItem('aiea-last-uid', firebaseUser.uid);
        } catch {
          /* private-mode / storage-disabled: skip the guard */
        }
        // Reason: when Firebase restores a persisted session on cold load,
        // roles haven't been re-fetched yet. The role-fetch effect seeds
        // rolesLoaded=true while user was still null, so without resetting it
        // here ProtectedRoute would evaluate stale rolesLoaded=true + empty
        // roles and bounce a role-bearing user to /try before adminMe() runs.
        // Resetting rolesLoaded in the SAME batched update as setUser keeps the
        // guard on the LoadingScreen until the real roles arrive. Fixes the
        // "/try pops up on every reload, must sign out + back in" bug.
        setRolesLoaded(false);
        setUser({
          id: firebaseUser.uid,
          email: firebaseUser.email,
          displayName: firebaseUser.displayName,
          photoURL: firebaseUser.photoURL,
          emailVerified: firebaseUser.emailVerified,
        });
      } else {
        setUser(null);
        setRoles([]);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, [setUser, setLoading, setRoles, setRolesLoaded]);

  // Fetch the caller's role set after auth so the sidebar can gate the
  // Admin link. /admin/me does NOT require admin — it just returns the
  // empty array for non-admin users, so this is safe to call always.
  // Reason: setRolesLoaded(true) is called in every branch (no user,
  // success, failure) so the route guard never waits indefinitely. See B-008.
  const setRolesFetchFailed = useAuthStore((s) => s.setRolesFetchFailed);
  useEffect(() => {
    if (!user) {
      setRoles([]);
      setRolesLoaded(true);
      setRolesFetchFailed(false);
      return;
    }
    let cancelled = false;
    (async () => {
      const { BackendAPI } = await import('./lib/api');
      // B-910: this effect runs ONCE per sign-in — `onAuthStateChanged` does not
      // fire on a token refresh, so `user` never changes and nothing re-runs it.
      // A single failure therefore hid the Admin tools for the WHOLE session, and
      // no amount of navigating brought them back, while the pages around it
      // recovered on their next fetch. So it retries rather than giving up: the
      // 401 path is already handled a layer down (api.ts forces a token refresh
      // and retries once), and this covers the rest — a backend still waking up,
      // a flaky connection, a request that raced the refresh.
      for (let attempt = 0; attempt < ROLE_FETCH_ATTEMPTS && !cancelled; attempt += 1) {
        try {
          const me = await BackendAPI.adminMe();
          if (!cancelled) {
            setRoles(me.roles || []);
            setRolesFetchFailed(false);
            setRolesLoaded(true);
          }
          return;
        } catch {
          if (attempt === ROLE_FETCH_ATTEMPTS - 1) break;
          // Let the guard render the app meanwhile rather than sit on a spinner:
          // roles are "unknown", which is what rolesFetchFailed already means.
          if (!cancelled) {
            setRolesFetchFailed(true);
            setRolesLoaded(true);
          }
          await new Promise((r) => setTimeout(r, ROLE_FETCH_RETRY_MS * (attempt + 1)));
        }
      }
      // Every attempt failed. Empty roles are read as "unknown", not as
      // "pay-per customer" — no forced V2, no trial bootstrap for a member on a
      // flaky connection.
      if (!cancelled) {
        setRoles([]);
        setRolesFetchFailed(true);
        setRolesLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, setRoles, setRolesLoaded, setRolesFetchFailed]);

  return (
    <>
      <BrowserRouter
        future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
      >
        <AppShell />
      </BrowserRouter>
      <Toaster />
    </>
  );
}

export default App;
